/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  FormControlLabel,
  IconButton,
  Pagination,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DarkUpperSection, LoadingSpinner, PageSection, StyledButton } from '../../../components';
import { useEffect, useState } from 'react';
import { LocationProps, UnknownLocation, LocationType } from '../../../datastore/locations';
import { useAlert, useCurrentUser, useDatastore, useIsOnline } from '../../../hooks';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import AddLocationForm from './modals/AddLocationModal';
import EditLocationForm from './modals/EditLocationModal';
import OfflineCard from '../../../components/cards/OfflineCard';
import { useLocation } from 'react-router-dom';
import { adminActionType } from '../../../datastore/adminActionHistory';
import dayjs from 'dayjs';
import { captureError } from '../../../common/sentry';

export default function ManageSubLocations() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const store = useDatastore();
  const { id } = useParams();
  const alert = useAlert();
  const user = useCurrentUser();
  const { isOnline } = useIsOnline();
  const [locations, setLocations] = useState<LocationProps[]>([]);
  const [selectedSubLocation, setSelectedSubLocation] = useState<LocationProps | null>(null);
  const [parentLocation, setParentLocation] = useState<LocationProps | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [openSubEditModal, setOpenSubEditModal] = useState(false);
  const [openParentEditModal, setOpenParentEditModal] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [showDisabled, setShowDisabled] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [openAddLocation, setOpenAddLocation] = useState(false);
  const locationName = location.state?.name;
  const locationAlias = location.state?.alias;
  const locationAddress = location.state?.address;

  useEffect(() => {
    async function fetchLocations() {
      setIsLoading(true);
      try {
        if (!id) {
          alert('Error retrieving Sub Locations, please refresh your page', 'error');
          return;
        }

        const result = await store.locations.getById(id);
        if (!result) {
          alert('Error retrieving Locations, please refresh your page', 'error');
          return;
        }
        setParentLocation(result);
        const filterUnknown = result?.subLocations?.filter((location) => location.name !== UnknownLocation.Name);

        if (!filterUnknown) {
          alert('No locations found', 'warning');
          return;
        }

        setLocations(filterUnknown);
      } catch (error) {
        console.error('Error fetching locations:', error);
        captureError(error, 'manageSubLocations-fetchLocations');
        alert('Error retrieving locations', 'error');
      }
      setIsLoading(false);
    }

    fetchLocations();
  }, [store.locations]);

  const filteredLocations = locations.filter(
    (location) =>
      location.name?.toLowerCase().includes(filterText?.toLowerCase()) && (showDisabled || !location.isDisabled)
  );

  const totalNumberOfPages = Math.ceil(filteredLocations.length / 25);
  const paginatedLocations = filteredLocations.slice((currentPage - 1) * 25, currentPage * 25);

  function handlePageChange(event: React.ChangeEvent<unknown>, value: number) {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleEditClick(location: LocationProps) {
    setSelectedSubLocation(location);
    setOpenSubEditModal(true);
  }

  async function submitLocationEditForm(data: LocationProps) {
    try {
      if (data.name === UnknownLocation.Name) {
        alert('Cannot set a locations name to "Unknown"', 'warning');
        return;
      }
      if (data.type === LocationType.mainLocation) {
        data.deliveryDays = Number(data.deliveryDays);
      }
      setLocations(locations.map((loc) => (loc.id === data.id ? data : loc)));

      const updatedLocation = await store.locations.update(data);
      const originalLocation = locations.find((loc) => loc.id === data.id);

      await store.adminActionHistories.create({
        id: crypto.randomUUID(),
        actionType: adminActionType.LOCATION_UPDATE,
        beforeAction: originalLocation ? JSON.stringify(originalLocation) : 'Error writing before action',
        afterAction: updatedLocation ? JSON.stringify(updatedLocation) : 'Error writing after action',
        actionBy: user.email,
        actionTime: dayjs()
      });

      alert('Updated Location', 'success');
    } catch (error) {
      console.log('Error updating location:', error);
      captureError(error, 'manageSubLocations-submitLocationEditForm');
      alert('Error updating location', 'error');
    } finally {
      setOpenSubEditModal(false);
      if (data.type === LocationType.mainLocation) {
        setSelectedSubLocation(null);
        setOpenParentEditModal(false);
      }
    }
  }

  async function handleAddSubLocation(data: LocationProps) {
    try {
      if (data.name === UnknownLocation.Name) {
        alert('Cannot create location with name "Unknown"', 'warning');
        return;
      }
      if (!id) {
        alert('Error creating Sub Location, please refresh your page and try again', 'error');
        return;
      }
      setLocations([...locations, data]);

      data.id = crypto.randomUUID();
      data.type = LocationType.subLocation;
      data.parentLocationId = id;

      const createdLocation = await store.locations.create(data);

      await store.adminActionHistories.create({
        id: crypto.randomUUID(),
        actionType: adminActionType.LOCATION_CREATE,
        beforeAction: null,
        afterAction: createdLocation ? JSON.stringify(createdLocation) : 'Error writing create action',
        actionBy: user.email,
        actionTime: dayjs()
      });

      alert('Created Location', 'success');
    } catch (error) {
      console.log('Error creating location:', error);
      captureError(error, 'manageSubLocations-handleAddSubLocation');
      alert('Error creating location', 'error');
    } finally {
      setOpenAddLocation(false);
    }
  }

  if (!user.isAdmin) return null;

  return (
    <>
      <DarkUpperSection>
        <Stack spacing={4} sx={{ padding: '1em 0' }}>
          <Stack direction="row" alignItems="center">
            <IconButton onClick={() => navigate('/settings/locations')}>
              <ArrowBackIcon fontSize="large" sx={{ color: theme.palette.primary.contrastText }} />
            </IconButton>
            <Typography variant="h2" fontWeight="bold" color={theme.palette.primary.contrastText}>
              Locations
            </Typography>
          </Stack>
          <Stack spacing={2} direction="row" justifyContent="space-between">
            <Stack>
              <Typography variant="h4" color={theme.palette.primary.contrastText}>
                {locationName} ({locationAlias})
              </Typography>
              <Typography sx={{ paddingTop: '0.5em' }} color={theme.palette.primary.contrastText}>
                {locationAddress}
              </Typography>
            </Stack>
            <Stack>
              <StyledButton variant="contained" onClick={() => setOpenParentEditModal(true)}>
                Edit Main Location
              </StyledButton>
            </Stack>
          </Stack>
        </Stack>
      </DarkUpperSection>
      {isOnline ? (
        <LoadingSpinner isLoading={isLoading}>
          <PageSection>
            <Stack spacing={4} sx={{ padding: '2em 0' }}>
              <TextField
                label="Filter by Name"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                variant="outlined"
                sx={{ flexGrow: 1 }}
              />
              <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ width: '100%' }}>
                <FormControlLabel
                  control={<Switch checked={showDisabled} onChange={(e) => setShowDisabled(e.target.checked)} />}
                  label="Show Disabled Sites"
                />
                <StyledButton variant="contained" onClick={() => setOpenAddLocation(true)}>
                  Add Sub Location
                </StyledButton>
              </Stack>
            </Stack>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>Name</b>
                  </TableCell>
                  <TableCell>
                    <b>Alias</b>
                  </TableCell>
                  <TableCell>
                    <b>Actions</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedLocations.map((location) => (
                  <TableRow key={location.id}>
                    <TableCell>{location.name}</TableCell>
                    <TableCell>{location.alias}</TableCell>
                    <TableCell>
                      <Button onClick={() => handleEditClick(location)}>Edit</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Stack spacing={2} direction="row" justifyContent="center" sx={{ paddingTop: '1em', paddingBottom: '1em' }}>
              <Pagination count={totalNumberOfPages} page={currentPage} onChange={handlePageChange} color="primary" />
            </Stack>
          </PageSection>
        </LoadingSpinner>
      ) : (
        <PageSection>
          <Stack justifyContent="center">
            <OfflineCard />
          </Stack>
        </PageSection>
      )}
      <EditLocationForm
        open={openSubEditModal}
        onClose={() => setOpenSubEditModal(false)}
        editLocationType={LocationType.subLocation}
        location={selectedSubLocation}
        onSubmit={submitLocationEditForm}
      />
      <AddLocationForm
        open={openAddLocation}
        addLocationType={LocationType.subLocation}
        onClose={() => setOpenAddLocation(false)}
        onSubmit={handleAddSubLocation}
      />
      <EditLocationForm
        open={openParentEditModal}
        editLocationType={LocationType.mainLocation}
        onClose={() => setOpenParentEditModal(false)}
        location={parentLocation}
        onSubmit={submitLocationEditForm}
      />
    </>
  );
}
